import React from 'react';
import { motion } from 'framer-motion';
import { User, Mail, LogOut, Settings, Shield, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Account: React.FC = () => {
  const { user, signOut } = useAuth();

  const handleSignOut = () => {
    signOut();
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Account <span className="gradient-text">Settings</span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-gray-300">
            Manage your BioAxis account preferences and security settings
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Section */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="glass-effect glass-effect-hover rounded-2xl p-8 shadow-glass mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 rounded-full bg-primary-500/10 dark:bg-primary-400/10">
                  <User className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Profile Information</h2>
                  <p className="text-slate-600 dark:text-gray-400">Your account details and preferences</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-gray-800/50">
                  <Mail className="w-5 h-5 text-slate-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1">
                      Email Address
                    </label>
                    <div className="text-lg text-slate-900 dark:text-white font-medium">
                      {user?.email || 'Not provided'}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-gray-500 mt-1">
                      This is your sign-in email address
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-gray-800/50">
                  <Shield className="w-5 h-5 text-slate-500" />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-1">
                      Account Status
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-lg text-slate-900 dark:text-white font-medium">Active</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-gray-500 mt-1">
                      Your account is in good standing
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Section */}
            <div className="glass-effect glass-effect-hover rounded-2xl p-8 shadow-glass">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 rounded-full bg-red-500/10">
                  <Shield className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Security</h2>
                  <p className="text-slate-600 dark:text-gray-400">Manage your account security settings</p>
                </div>
              </div>

              <div className="space-y-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-gray-300 font-medium hover:bg-white/70 dark:hover:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5" />
                    <span>Change Password</span>
                  </div>
                  <span className="text-sm text-slate-500">•••••••••</span>
                </motion.button>

                <motion.button
                  onClick={handleSignOut}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center justify-center gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 text-red-700 dark:text-red-300 font-medium hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Quick Actions Sidebar */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="glass-effect glass-effect-hover rounded-2xl p-6 shadow-glass">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <motion.a
                  href="/plans"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="block w-full flex items-center gap-3 p-3 rounded-xl border border-slate-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-gray-300 font-medium hover:bg-white/70 dark:hover:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                >
                  <CreditCard className="w-5 h-5" />
                  <span>Manage Plans</span>
                </motion.a>

                <motion.a
                  href="/dashboard"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="block w-full flex items-center gap-3 p-3 rounded-xl border border-slate-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-gray-300 font-medium hover:bg-white/70 dark:hover:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                >
                  <Settings className="w-5 h-5" />
                  <span>Back to Dashboard</span>
                </motion.a>
              </div>

              {/* Account Stats */}
              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-3">Account Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-gray-400">Member since</span>
                    <span className="text-slate-900 dark:text-white font-medium">
                      {user?.metadata?.creationTime ?
                        new Date(user.metadata.creationTime).toLocaleDateString() :
                        'Recently'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-gray-400">Last sign in</span>
                    <span className="text-slate-900 dark:text-white font-medium">
                      {user?.metadata?.lastSignInTime ?
                        new Date(user.metadata.lastSignInTime).toLocaleDateString() :
                        'Today'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Account;

